import { useEffect, useRef, useState } from "react"
import { Linkedin, Mail } from "lucide-react"

const leaders = [
  {
    name: "Udaya Vanteru",
    role: "President",
    bio: "Udaya brings over two decades of experience leading enterprise technology modernization and large-scale digital initiatives. As the driving force behind RSA Tech Group's evolution, she has built the organization into a trusted Managed Services and Digital Engineering partner for complex enterprises.",
    bio2: "With a deep understanding of platform transformation, cloud ecosystems, and AI-enabled operations, Udaya focuses on aligning technology strategy with long-term business outcomes.",
    image: "/udaya-vanteru.jpg",
    linkedin: "https://www.linkedin.com/in/udaya-vanteru-35010928a/",
    email: "udaya.v@rsatech.com",
    accent: "from-blue-500 to-blue-700",
    accentSolid: "bg-blue-500",
    tagBg: "bg-blue-500/20 text-blue-300 ring-blue-500/30",
  },
  {
    name: "Srini Ragipally",
    role: "CEO / Interim COO",
    bio: "Srini leads RSA Tech Group's global operations with a focus on delivery excellence, platform reliability, and scalable execution models. He oversees managed services operations, enterprise program governance, and operational transformation initiatives across multiple industries.",
    bio2: "With extensive experience in running complex, multi-platform environments, Srini ensures seamless coordination between engineering, cloud operations, and customer success.",
    image: "/Srinivas Ragipally.jpg",
    linkedin: "https://www.linkedin.com/in/sragipally/",
    email: "srini.r@rsatech.com",
    accent: "from-emerald-500 to-emerald-700",
    accentSolid: "bg-emerald-500",
    tagBg: "bg-emerald-500/20 text-emerald-300 ring-emerald-500/30",
  },
  {
    name: "Vijay Simhachalam",
    role: "Director of Global Operations",
    bio: "Vijay leads Connexr's global operations strategy with a focus on delivery governance, cross-functional execution, and scalable operating models. He works across regions to ensure consistent service quality, resilient workflows, and strong customer outcomes.",
    bio2: "With deep experience in enterprise operations and transformation programs, Vijay drives execution discipline while enabling teams to deliver faster, smarter, and with measurable business impact.",
    image: "/vijay-simhachalam.png",
    email: "vijay.s@rsatech.com",
    accent: "from-violet-500 to-violet-700",
    accentSolid: "bg-violet-500",
    tagBg: "bg-violet-500/20 text-violet-300 ring-violet-500/30",
  },
  {
    name: "Santthosh Purmani",
    role: "CIO / CMO / IT Managing Director",
    bio: "Santthosh leads enterprise technology strategy and large-scale digital transformation at RSA Tech Group. With deep expertise in cloud platforms and AI-driven systems, he plays a pivotal role in translating complex business challenges into scalable, intelligent technology solutions.",
    bio2: "Combining strategic oversight with a practitioner's mindset, Santthosh oversees end-to-end program execution and operational excellence.",
    image: "/IMG_6220.jpg",
    linkedin: "https://www.linkedin.com/in/spurmani/",
    email: "santhosh@rsatechgroup.com",
    accent: "from-cyan-500 to-cyan-700",
    accentSolid: "bg-cyan-500",
    tagBg: "bg-cyan-500/20 text-cyan-300 ring-cyan-500/30",
  },
  {
    name: "Suhaib Ahmed",
    role: "Regional IT Sales Head - APAC/EMA",
    bio: "Suhaib leads regional delivery and sales execution with a strong focus on customer outcomes, growth acceleration, and operational discipline. He partners closely with enterprise stakeholders to align go-to-market strategy with measurable business impact.",
    bio2: "With a practical blend of delivery leadership and sales enablement, Suhaib helps teams scale engagements, improve conversion quality, and build long-term client trust across strategic accounts.",
    image: "/suhaib-ahmed.png",
    linkedin: "https://www.linkedin.com/in/suhaib-ahmed-aa04a7b6/",
    email: "suhaib.ahmed@rsatech.com",
    accent: "from-amber-500 to-amber-700",
    accentSolid: "bg-amber-500",
    tagBg: "bg-amber-500/20 text-amber-300 ring-amber-500/30",
  },
]

export const LeadershipSection = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (!wrapperRef.current) return
      const rect = wrapperRef.current.getBoundingClientRect()
      const sectionHeight = wrapperRef.current.offsetHeight
      const viewportHeight = window.innerHeight

      // How far we've scrolled into the section (0 = top just hit viewport, max = bottom leaving)
      const scrolled = -rect.top
      // Total scrollable distance within the wrapper
      const scrollable = sectionHeight - viewportHeight
      if (scrollable <= 0) return

      const progress = Math.max(0, Math.min(1, scrolled / scrollable))
      const newIndex = Math.min(
        leaders.length - 1,
        Math.floor(progress * leaders.length)
      )
      setActiveIndex(newIndex)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const activeLeader = leaders[activeIndex]

  return (
    <section id="leadership" className="relative bg-[#0a0f1c]">
      {/* Tall wrapper - scroll distance drives the card transitions */}
      <div
        ref={wrapperRef}
        style={{ height: `${leaders.length * 100}vh` }}
        className="relative"
      >
        {/* Sticky viewport - pinned while scrolling through the wrapper */}
        <div className="sticky top-0 h-screen overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1c] via-[#0d1424] to-[#0a0f1c]" />
            <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[200px]" />
            <div className="absolute bottom-1/4 left-0 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[200px]" />
          </div>

          <div className="relative z-10 h-full flex flex-col">
            {/* Section Header */}
            <div className="pt-20 pb-6 text-center shrink-0">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-white tracking-tight leading-tight px-4">
                The Minds Behind the{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                  Innovation
                </span>
              </h2>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mx-auto mb-4" />
              <p className="text-slate-400 text-base md:text-lg font-light leading-relaxed max-w-xl mx-auto px-4">
                A leadership team shaping the future of enterprise platforms and intelligent systems.
              </p>
            </div>

            {/* Main content area - image left, cards right */}
            <div className="flex-1 min-h-0 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-8">
              <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
                {/* LEFT: Image */}
                <div className="hidden lg:flex items-center">
                  <div className="relative w-full aspect-[3/4] max-h-[520px] rounded-3xl overflow-hidden shadow-2xl shadow-black/50">
                    {leaders.map((leader, index) => (
                      <div
                        key={index}
                        className="absolute inset-0 transition-all duration-700 ease-in-out"
                        style={{
                          opacity: activeIndex === index ? 1 : 0,
                          transform:
                            activeIndex === index
                              ? "scale(1)"
                              : activeIndex > index
                              ? "scale(0.95)"
                              : "scale(1.05)",
                        }}
                      >
                        <img
                          src={leader.image}
                          alt={leader.name}
                          className={`w-full h-full object-cover ${
                            leader.name === "Santthosh Purmani"
                              ? "object-[50%_25%]"
                              : leader.name === "Suhaib Ahmed"
                              ? "object-[50%_20%]"
                              : "object-[50%_15%]"
                          }`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-transparent to-transparent opacity-80" />
                      </div>
                    ))}

                    {/* Floating info on image */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                      <div
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-2 ring-1 ${activeLeader.tagBg}`}
                      >
                        {activeLeader.role}
                      </div>
                      <h3 className="text-2xl font-bold text-white tracking-tight">
                        {activeLeader.name}
                      </h3>
                      <div className="flex gap-3 mt-3">
                        {"linkedin" in activeLeader && activeLeader.linkedin ? (
                          <a
                            href={activeLeader.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/10 rounded-full hover:bg-blue-600 text-white transition-all backdrop-blur-md"
                          >
                            <Linkedin className="w-4 h-4" />
                          </a>
                        ) : null}
                        <a
                          href={`mailto:${activeLeader.email}`}
                          className="p-2 bg-white/10 rounded-full hover:bg-blue-600 text-white transition-all backdrop-blur-md"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Card stack - cards slide up in place */}
                <div className="flex items-center">
                  <div className="w-full relative">
                    {/* Progress dots */}
                    <div className="flex gap-2 mb-6">
                      {leaders.map((leader, index) => (
                        <div
                          key={index}
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            activeIndex === index
                              ? `w-10 ${leader.accentSolid}`
                              : activeIndex > index
                              ? "w-3 bg-white/25"
                              : "w-3 bg-white/10"
                          }`}
                        />
                      ))}
                    </div>

                    {/* Card container - overflow hidden, cards slide up */}
                    <div className="relative overflow-hidden rounded-3xl" style={{ height: "420px" }}>
                      <div
                        className="transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                        style={{
                          transform: `translateY(-${activeIndex * 420}px)`,
                        }}
                      >
                        {leaders.map((leader, index) => (
                          <div
                            key={index}
                            className="h-[420px] p-6 md:p-8"
                            style={{
                              background:
                                activeIndex === index
                                  ? "rgba(255,255,255,0.03)"
                                  : "transparent",
                            }}
                          >
                            {/* Mobile image */}
                            <div className="lg:hidden mb-4">
                              <div className="relative h-40 rounded-2xl overflow-hidden">
                                <img
                                  src={leader.image}
                                  alt={leader.name}
                                  className={`w-full h-full object-cover ${
                                    leader.name === "Santthosh Purmani"
                                      ? "object-[50%_25%]"
                                      : leader.name === "Suhaib Ahmed"
                                      ? "object-[50%_20%]"
                                      : "object-[50%_15%]"
                                  }`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c]/80 to-transparent" />
                              </div>
                            </div>

                            {/* Counter */}
                            <div className="flex items-center gap-4 mb-4">
                              <span
                                className={`text-5xl md:text-6xl font-black bg-gradient-to-b ${leader.accent} bg-clip-text text-transparent opacity-25 leading-none`}
                              >
                                {String(index + 1).padStart(2, "0")}
                              </span>
                              <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
                            </div>

                            {/* Role tag */}
                            <div
                              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-3 ring-1 ${leader.tagBg}`}
                            >
                              {leader.role}
                            </div>

                            {/* Name */}
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-tight">
                              {leader.name}
                            </h3>

                            {/* Bio */}
                            <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-3">
                              {leader.bio}
                            </p>
                            <p className="text-slate-400 text-sm leading-relaxed">
                              {leader.bio2}
                            </p>

                            {/* Mobile social links */}
                            <div className="flex gap-3 mt-4 lg:hidden">
                              {"linkedin" in leader && leader.linkedin ? (
                                <a
                                  href={leader.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-blue-600 rounded-xl text-slate-400 hover:text-white transition-all text-sm border border-white/10"
                                >
                                  <Linkedin className="w-4 h-4" />
                                  <span>LinkedIn</span>
                                </a>
                              ) : null}
                              <a
                                href={`mailto:${leader.email}`}
                                className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-blue-600 rounded-xl text-slate-400 hover:text-white transition-all text-sm border border-white/10"
                              >
                                <Mail className="w-4 h-4" />
                                <span>Email</span>
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Step counter */}
                    <div className="mt-4 flex items-center justify-between text-slate-500 text-sm">
                      <span>
                        <span className="text-white font-bold text-lg">{String(activeIndex + 1).padStart(2, "0")}</span>
                        <span className="mx-2">/</span>
                        <span>{String(leaders.length).padStart(2, "0")}</span>
                      </span>
                      <span className="text-slate-600 text-xs uppercase tracking-widest">Scroll to explore</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
